import React, { useRef, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { RiAlertLine, RiErrorWarningLine } from "react-icons/ri";

export default function DraggableControlPanel({
  detectiveTitles = [],
  mitigativeTitles = [],
  visible = true,
  pos = { x: 24, y: 24 },
  setPos = () => {},
  onClose,
}) {
  const { t } = useTranslation();
  const startRef = useRef(null);
  const panelRef = useRef(null);
  const [hovering, setHovering] = useState(false);

  // GLOBAL wheel guard while hovering the panel
  useEffect(() => {
    if (!hovering) return;
    const el = panelRef.current;
    if (!el) return;

    const blockWheel = (e) => {
      // only block if event originated within the panel
      if (el.contains(e.target)) {
        // allow the panel to scroll but stop it from reaching the canvas zoom
        e.stopImmediatePropagation?.();
        e.stopPropagation();
        // also prevent ctrl+wheel page zoom from the browser
        if (e.ctrlKey) e.preventDefault();
      }
    };

    // capture + non-passive so we can preventDefault for ctrl+wheel
    window.addEventListener("wheel", blockWheel, { capture: true, passive: false });
    return () => window.removeEventListener("wheel", blockWheel, { capture: true });
  }, [hovering]);

  const norm = (arr) =>
    (arr ?? []).map((it) => {
      if (typeof it === "string") {
        return { id: null, title: it, efficiency: null };
      }

      const id =
        it.id ??
        it.control_id ??
        it.ControlID ??
        null;

      const title =
        it.title ??
        it.control_details ??
        it.ControlDetails ??
        "";

      const efficiency =
        it.efficiency ??
        it.EfficiencyLevel ??
        it.efficiency_level ??
        null;

      return { id, title, efficiency };
    });

  const det = norm(detectiveTitles);
  const mit = norm(mitigativeTitles);

  ////console.log("Controsl", det, "\n" ,mit)
    
  if (!visible) return null;

  const beginDrag = (e) => {
    startRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      origX: pos.x,
      origY: pos.y,
    };
    document.addEventListener("mousemove", onDrag);
    document.addEventListener("mouseup", endDrag);
  };
  const onDrag = (e) => {
    const s = startRef.current;
    if (!s) return;
    setPos({ x: s.origX + (e.clientX - s.startX), y: s.origY + (e.clientY - s.startY) });
  };
  const endDrag = () => {
    startRef.current = null;
    document.removeEventListener("mousemove", onDrag);
    document.removeEventListener("mouseup", endDrag);
  };

  const handleDragStart = (e, type, item) => {
    e.dataTransfer.setData(
      "application/json",
      JSON.stringify({ type, control_id: item.id, text: item.title, efficiency: item.efficiency })
    );
    e.dataTransfer.effectAllowed = "copy";
  };

  // cap to ~5 rows then scroll
  const TOTAL = det?.length + mit?.length;
  const MAX_ROWS = 5;
  const ROW_H = 42;
  const EXTRA = 72;
  const maxBodyH = Math.min(TOTAL, MAX_ROWS) * ROW_H + EXTRA;

  return (
    <div
      ref={panelRef}
      className="fixed z-[9999] w-[340px] bg-white shadow-lg border rounded-md flex flex-col pointer-events-auto"
      style={{ left: pos.x, top: pos.y }}
      onMouseEnter={() => setHovering(true)}
      onMouseLeave={() => setHovering(false)}
    >
      {/* Header */}
      <div
        className="cursor-move select-none flex items-center justify-between px-3 py-2 rounded-t-md bg-gray-100 border-b"
        onMouseDown={beginDrag}
      >
        <div className="text-sm font-semibold">{t("bowtieAnalysis.controlPanel.title")}</div>
        {onClose && (
          <button
            className="text-xs px-2 py-1 rounded hover:bg-gray-200"
            onClick={onClose}
            type="button"
          >
            {t("bowtieAnalysis.controlPanel.hide")}
          </button>
        )}
      </div>

      {/* Body (scrolls after 5 controls) */}
      <div
        className="p-4 flex-1 overflow-y-auto overscroll-contain"
        style={{ maxHeight: maxBodyH, touchAction: "manipulation" }}
        // belt & suspenders: also block wheel at React capture on the body
        onWheelCapture={(e) => {
          e.stopPropagation();
          if (e.ctrlKey) e.preventDefault();
        }}
      >
        {/* Preventive */}
        <div className="mb-4">
          <div className="text-sm font-semibold text-gray-600 mb-1">{t("bowtieAnalysis.controlPanel.preventive")}</div>
          <div className="flex flex-col gap-2">
            {det.map((item, i) => (
              <div
                key={item.id ?? `det-${i}`}
                draggable
                onDragStart={(e) => handleDragStart(e, "Preventive", item)}
                className="h-10 p-2 border rounded-md bg-white shadow-sm cursor-grab active:cursor-grabbing text-sm hover:bg-gray-50"
                title={item.title}
              >
                <div className="flex items-center gap-2 h-full">
                  <RiAlertLine className="opacity-60" />
                  <span className="truncate">{item.title}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Mitigative */}
        <div>
          <div className="text-sm font-semibold text-gray-600 mb-1">{t("bowtieAnalysis.controlPanel.mitigative")}</div>
          <div className="flex flex-col gap-2">
            {mit.map((item, i) => (
              <div
                key={item.id ?? `mit-${i}`}
                draggable
                onDragStart={(e) => handleDragStart(e, "Mitigative", item)}
                className="h-10 p-2 border rounded-md bg-white shadow-sm cursor-grab active:cursor-grabbing text-sm hover:bg-gray-50"
                title={item.title}
              >
                <div className="flex items-center gap-2 h-full">
                  <RiErrorWarningLine className="opacity-60" />
                  <span className="truncate">{item.title}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
